import { useEffect, useState } from "react";
import { Modal } from "./Modal";
import { kanbanOn, watchKanbanLink } from "./kanbanLink";
import { alignWeekMode, importWeek, loadCrmWeek, setWeekMode, wipeKanbanTasks } from "./week";

export function KanbanLink() {
  const [on, setOn] = useState(kanbanOn);
  const [open, setOpen] = useState(false);
  useEffect(() => watchKanbanLink(() => setOn(kanbanOn())), []);
  useEffect(() => {
    void alignWeekMode().catch(() => undefined);
  }, []);
  return (
    <>
      <button
        type="button"
        className="crm-pill ml-auto inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-semibold"
        style={on ? { borderColor: "var(--crm-accent, #3b82f6)", color: "var(--crm-accent-strong, #2563eb)" } : undefined}
        onClick={() => setOpen(true)}
      >
        {on ? "Connecté" : "Autonome"}
      </button>
      {open ? <KanbanSync onClose={() => setOpen(false)} /> : null}
    </>
  );
}

const STEPS = ["Importation des tâches", "Préparation des propriétés", "Préparation des rappels", "Vérification"];

export function KanbanSync({ onClose }: { onClose: () => void }) {
  const [properties, setProperties] = useState(false);
  const [reminders, setReminders] = useState(true);
  const [phase, setPhase] = useState<"form" | "run" | "done" | "error">("form");
  const [step, setStep] = useState(0);
  const [result, setResult] = useState({ tasks: 0, properties: 0, reminders: 0 });
  const [imported, setImported] = useState(0);
  const [removed, setRemoved] = useState<number | null>(null);
  const [sure, setSure] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const linked = kanbanOn();

  useEffect(() => {
    let alive = true;
    void loadCrmWeek().then((board) => { if (alive) setImported(board.importedCount); }).catch(() => undefined);
    return () => { alive = false; };
  }, []);

  async function run() {
    setError("");
    setRemoved(null);
    setSure(false);
    setPhase("run");
    setStep(0);
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const duration = reduce ? 1200 : 10000;
    const started = Date.now();
    const timers = STEPS.map((_, index) => window.setTimeout(() => setStep(index), Math.round((duration / STEPS.length) * index)));
    try {
      const data = await importWeek({ properties, reminders });
      const wait = Math.max(0, duration - (Date.now() - started));
      await new Promise((resolve) => window.setTimeout(resolve, wait));
      setResult(data);
      setImported(data.tasks);
      setPhase("done");
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "La synchro a échoué.");
      setPhase("error");
    } finally {
      timers.forEach((timer) => window.clearTimeout(timer));
    }
  }

  async function mode(next: boolean) {
    setBusy(true);
    setError("");
    try {
      await setWeekMode(next);
      onClose();
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Changement impossible.");
    } finally {
      setBusy(false);
    }
  }

  async function wipe() {
    if (!sure) {
      setSure(true);
      return;
    }
    setBusy(true);
    setError("");
    try {
      setRemoved(await wipeKanbanTasks());
      setSure(false);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Mes tâches n’a pas pu être vidé.");
    } finally {
      setBusy(false);
    }
  }

  const stepLabel = STEPS[step] === "Préparation des propriétés" && !properties
    ? "Importance déjà en place"
    : STEPS[step] === "Préparation des rappels" && !reminders
      ? "Rappels laissés de côté"
      : STEPS[step];

  return (
    <Modal onClose={onClose} sheetClass="crm-pop w-full max-w-lg rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
      {(close) => (
        <div>
          <div className="mb-4">
            <h2 className="text-lg font-semibold">Synchro Mes tâches</h2>
            <p className="mt-1 text-sm text-slate-500">Importance teinte la carte, comme aujourd’hui. Elle est déjà dans le CRM.</p>
          </div>
          {phase === "form" ? (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked disabled /> Tâches, mise en attente et périodicité</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={properties} onChange={(event) => setProperties(event.target.checked)} /> Propriétés, en plus d’Importance</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={reminders} onChange={(event) => setReminders(event.target.checked)} /> Rappels e-mail et SMS</label>
              <button type="button" className="crm-fill mt-2 w-full rounded-full px-4 py-2 text-sm font-semibold" onClick={() => void run()}>Importer</button>
            </div>
          ) : null}
          {phase === "run" ? (
            <div role="status" aria-live="polite">
              <div className="crm-sync-bar is-run"><span /></div>
              <p className="mt-3 text-sm font-medium">{stepLabel}</p>
              <ul className="mt-3 space-y-1 text-sm text-slate-500">
                {STEPS.map((label, index) => <li key={label} className={index <= step ? "text-slate-800" : undefined}>{index < step ? "✓ " : index === step ? "… " : ""}{label}</li>)}
              </ul>
            </div>
          ) : null}
          {phase === "done" ? (
            <div className="space-y-2 text-sm">
              <p className="text-base font-semibold">{result.tasks} tâche{result.tasks > 1 ? "s" : ""} importée{result.tasks > 1 ? "s" : ""}</p>
              <p className="text-slate-500">{properties ? `${result.properties} propriété${result.properties > 1 ? "s" : ""}` : "Importance seule"} · {reminders ? `${result.reminders} rappel${result.reminders > 1 ? "s" : ""}` : "rappels non repris"}</p>
              <p className="text-slate-500">On valide ce total avant de vider Mes tâches. La copie reste dans le CRM.</p>
            </div>
          ) : null}
          {phase === "error" ? <p className="text-sm text-rose-700">{error}</p> : null}
          {error && phase !== "error" ? <p className="mt-3 text-sm text-rose-700">{error}</p> : null}
          {removed !== null ? <p className="mt-3 text-sm font-medium">Mes tâches est vierge. {removed} tâche{removed > 1 ? "s" : ""} retirée{removed > 1 ? "s" : ""}.</p> : null}
          {phase !== "run" ? (
            <div className="mt-4 flex flex-wrap gap-2">
              {linked ? <button type="button" className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold" disabled={busy} onClick={() => void mode(false)}>Passer en autonome</button> : <button type="button" className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold" disabled={busy} onClick={() => void mode(true)}>Revoir Mes tâches</button>}
              {imported > 0 || phase === "done" ? (
                <button type="button" className="rounded-full border border-rose-200 px-3 py-1.5 text-sm font-semibold text-rose-700" disabled={busy} onClick={() => void wipe()}>{sure ? "Confirmer le vidage" : "Vider Mes tâches"}</button>
              ) : null}
              {phase === "error" ? <button type="button" className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold" onClick={() => setPhase("form")}>Revenir</button> : null}
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
